import { reconstitutedException } from '@mimir/common/util/exception';
import type { SourceLocationResolver } from './extendScriptSourceLocation';

const ARGS_PREVIEW_LIMIT = 500;

interface ExtendScriptErrorPayload {
  message?: string;
  name?: string;
  line?: number;
  [key: string]: unknown;
}

interface ExtendScriptCallReply {
  result?: unknown;
  error?: ExtendScriptErrorPayload;
}

export function parseExtendScriptCallReply(reply: string, callName: string): ExtendScriptCallReply {
  try {
    return JSON.parse(reply);
  } catch (_) {
    throw new Error(`${callName} failed: ${reply}`);
  }
}

// err.line is only meaningful when the error was thrown inside the bundle
// whose line map resolveSourceLocation reads.
export async function extendScriptCallError(
  err: ExtendScriptErrorPayload,
  action: string,
  args: unknown[],
  resolveSourceLocation: SourceLocationResolver,
): Promise<Error> {
  const location = err.line == null ? undefined : await resolveSourceLocation(err.line);
  let locationText: string | undefined;
  if (location) {
    locationText = `${location.source}:${location.line}`;
  } else if (err.line != null) {
    locationText = `bundle line ${err.line}`;
  }
  const argsPretty = JSON.stringify(args, null, 2);
  const argsPreview = argsPretty.length > ARGS_PREVIEW_LIMIT ? `${argsPretty.slice(0, ARGS_PREVIEW_LIMIT)}…` : argsPretty;
  // The message can reach a user-facing toast, so diagnostics travel as separate fields.
  const exception = reconstitutedException({
    ...err,
    name: err.name ?? 'ExtendScriptError',
    message: err.message ?? `${action} failed`,
    nleMethod: action,
    ...(locationText !== undefined && { location: locationText }),
  });
  if (typeof exception.stack === 'string') {
    // Drop the native header line and the reconstitutedException frame
    // right below it (new Error() is constructed inside that function,
    // one frame deeper than any of its callers) - keeping this local
    // instead of changing reconstitutedException for every other caller.
    const [, , ...rest] = exception.stack.split('\n');
    const contextLine = `ExtendScript error in ${action}(${argsPreview})${locationText === undefined ? '' : ` at ${locationText}`} ${exception.name}: ${exception.message}`;
    const frame = location ? [`    at ${action} (${location.source}:${location.line})`] : [];
    exception.stack = [contextLine, ...frame, ...rest].join('\n');
  }
  return exception;
}
